import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import get from 'lodash/get';
import camelCase from 'lodash/camelCase'

/**
 * Text and Image Section v2, now with better spacing. Used for SMB section.
 */

// classnames for Contentful widths
const WIDTH_CLASS = {
  '25%': 'quarter',
  '33%': 'third',
  '50%': 'half',
  '66%': 'two-thirds',
  '75%': 'three-quarters',
  '100%': 'one',
};

// helper for retrieving from WIDTH_CLASS map
const getWidthClass = percent => get(WIDTH_CLASS, percent);

// Width class for text widths.
const OPPOSITE_WIDTH_CLASS = {
  quarter: 'three-quarters',
  third: 'two-thirds',
  half: 'half',
  'two-thirds': 'third',
  'three-quarters': 'quarter',
  one: 'two-thirds',
};

// helper for retreiving from OPPOSITE_WIDTH_CLASS map
const getRemainingWidth = widthClass => get(OPPOSITE_WIDTH_CLASS, widthClass);

/**
 * Helper for rendering Header.
 */
const HeaderElement = ({
  className,
  title,
  subTitle,
}) => (
  <div className={className}>
    {title}
    {subTitle}
  </div>
);

HeaderElement.propTypes = {
  className: PropTypes.string,
  title: PropTypes.node,
  subTitle: PropTypes.node,
};

HeaderElement.defaultProps = {
  className: null,
  title: null,
  subTitle: null,
};

/**
 * UI for text and image section.
 * This is one section with two distinct containers.
 * The text container contains a header, some Markdown content, and a CTA.
 * The Image container contains a fluid image.
 *
 * There are 4 options for desktop layout:
 *  - Top Image Card
 *  - Bottom Image Card
 *  - Left Image Card
 *  - Right Image Card
 * On Mobile, these sections become one organized like:
 *  - Header
 *  - Image
 *  - Content
 *
 * @param {String} className Class given to the Text and Image section
 * @param {Node}   ctaButton Button react node
 * @param {String} ctaPosition Class determining CTA horizontal alignment
 * @param {String} displayType where to display the header on desktop.
 *  Mobile it's always on top.
 * @param {String} headerLocation See PropTypes for options.
 * @param {String} image Image url
 * @param {String} imageWidth See PropTypes for options.
 * @param {String} subTitle sub header
 * @param {String} title Main header
 * @param {Node}   wistiaButton Wistia Button react node
 */
const TextAndImageSection = ({
  className,
  ctaButton,
  ctaPosition,
  displayType,
  headerLocation,
  image,
  imageWidth,
  subTitle: subTitleText,
  bodyContent,
  title: titleText,
  wistiaButton,
}) => {
  const title = titleText && <h2 className='title'>{titleText}</h2>;

  const subTitle = subTitleText && <h3 className='subTitle'>{subTitleText}</h3>;

  const isInlineHeader = headerLocation === 'inline';
  // Render two headers if inline. Mobile header displays topLeft,
  //  desktop displays inline
  const headerClasses = isInlineHeader ? 'header inlineTop' : `header ${camelCase(headerLocation)}`;
  const header = (title || subTitle) && (
    <HeaderElement
      className={headerClasses}
      title={title}
      subTitle={subTitle}
    />
  );

  // Desktop inline header
  const inlineHeader = isInlineHeader ? (
    <HeaderElement
      className='header inline'
      title={title}
      subTitle={subTitle}
    />
  ) : null;

  // Determine width of image
  const imageWidthClass = imageWidth ? getWidthClass(imageWidth) : '';

  // Text takes up remaining width
  const textSize = getRemainingWidth(imageWidthClass);

  const imageElement = image && <img src={image} alt='' className='image' />;

  const textContent = bodyContent && <div className='textContent'>{bodyContent}</div>;

  return (
    <div className={classnames('TextAndImageSection-beta', className, displayType)}>
      { header }
      <div className={classnames('contentWrapper', displayType)}>
        <div className={classnames('imageContainer', imageWidthClass)}>
          {imageElement}
          {wistiaButton}
        </div>
        <div className={classnames('textContainer', textSize)}>
          { inlineHeader }
          { textContent }
          <div className={classnames('ctaWrapper', `ctaWrapper-${ctaPosition}`)}>{ ctaButton }</div>
        </div>
      </div>
    </div>
  );
};

export const textAndImageSectionPropTypes = {
  className: PropTypes.string,
  ctaButton: PropTypes.node,
  ctaPosition: PropTypes.oneOf(['left', 'center', 'right']),
  displayType: PropTypes.oneOf(
    ['leftImageCard', 'rightImageCard', 'topImageCard', 'bottomImageCard']
    ),
  headerLocation: PropTypes.oneOf(['inline', 'topCenter', 'topLeft']),
  image: PropTypes.string,
  imageWidth: PropTypes.string,
  subTitle: PropTypes.string,
  bodyContent: PropTypes.node,
  title: PropTypes.string,
  wistiaButton: PropTypes.node,
};

TextAndImageSection.propTypes = textAndImageSectionPropTypes;

TextAndImageSection.defaultProps = {
  className: '',
  ctaButton: null,
  ctaPosition: 'left',
  displayType: 'leftImageCard',
  headerLocation: 'inline',
  image: null,
  imageWidth: null,
  subTitle: null,
  bodyContent: null,
  title: null,
  wistiaButton: null,
};

/*
Helpers for left and right image cards.
*/

const leftGridContentCSS = css`
  // Left grid content mixin/helper
  // On mobile, falls back to top image card.
  // Half is default on desktop. 2/3 and 3/4 only on desktop.
  // disabling because these are mixins, not definitions.
  @media (min-width: 770px) {
    grid-row: 1;
    grid-column-start: 1;
    grid-column-end: 7;
    &.one {
      grid-column-end: 13;
    }
  }

  @media (min-width: 960px) {
    &.quarter {
      grid-column-end: 4;
    }
    &.third {
      grid-column-end: 5;
    }
    &.two-thirds {
      grid-column-end: 9;
    }
    &.three-quarters {
      grid-column-end: 10;
    }
  }
`

const rightGridContentCSS = css`
  // Right grid content mixin/helper
  // On mobile, falls back to top image card.
  // disabling because these are mixins, not definitions.
  @media (min-width: 770px) {
    grid-row: 1;
    grid-column-end: 13;
    grid-column-start: 7;
    &.one {
      grid-column-start: 1;
    }
  }

  @media (min-width: 960px) {
    &.quarter {
      grid-column-start: 10;
    }
    &.third {
      grid-column-start: 9;
    }
    &.two-thirds {
      grid-column-start: 5;
    }
    &.three-quarters {
      grid-column-start: 4;
    }
  }
`

export default styled(TextAndImageSection)`
  // Header section
  .header {
    margin-bottom: 2rem;

    .title {
      text-align: inherit;
      font-family: 'Space Grotesk', sans-serif;;
      font-size: 2.25rem;
      line-height: 2.25rem;

      @media (min-width: 770px) {
        font-family: 'Space Grotesk', sans-serif;;
        line-height: 3.3rem;
        font-size: 3rem;
      }
    }

    .subTitle {
      font-family: 'Space Grotesk', sans-serif;;
      font-family: 'Space Grotesk', sans-serif;;
      font-size: 1.5rem;
      line-height: 1.8rem;

      font-weight: 700;
      // using margin-top, as we don't want extra margin if no subtitle
      margin-top: 2rem;
      text-align: inherit;
    }

    &.inline {
      display: none;

      @media (min-width: 770px) {
        display: block;
        text-align: left;
      }
    }

    // An inline header appears as topCenter on mobile, emulates a top image card.
    &.inlineTop {
      text-align: center;
      @media (min-width: 770px) {
        display: none;
      })
    }

    &.topCenter {
      text-align: center;
    }

    &.topLeft {
      text-align: left;
    }
  }

  // Wrapper to align CTA
  // Mobile is always centered
  // Desktop defaults to left aligned, has options via Contentful
  .ctaWrapper {
    display: flex;
    justify-content: center;
    // match header bottom margin
    margin-top: 1rem;

    @media (min-width: 770px) {
      justify-content: flex-start;
      &-center {
        justify-content: center;
      }
      &-right {
        justify-content: flex-end;
      }
    }
  }

  // Make sure images don't overflow parent
  .imageContainer {
    position: relative;

    img {
      margin: 0 auto;
      width: 100%;
      max-width: 100% !important;
      display: block; // remove white space below img
    }
  }

  /*
    There are two children of contentWrapper
    There are four layout possibilities for desktop:
      .leftImageCard
        Image | Text
      .rightImageCard
        Text | Image
      .topImageCard
        Image
        -----
        Text
      .bottomImageCard
        Text
        -----
        Image

    .leftImageCard and .rightImageCard can have content distributed like:
      Desktop:
        25% | 75%
        33% | 66%
        50% | 50%
        66% | 33%
        75% | 25%
      Tablet: On tablet, we fall back to 50% | 50% so neither left or right content is too small.
      Mobile: left and rightImageCard become a topImageCard with image width 100%.

     .topImageCard and .bottomImageCard use image widths only; Text always takes up
     100% of container width. Image widths can be 25%, 33%, 50%, 66%, 75%, 100%.
     On mobile, image widths are always 100%.

  */
  .contentWrapper {
    display: grid;
    grid-gap: 2rem;
    grid-template-rows: max-content 1fr;

    &.bottomImageCard,
    &.topImageCard {
      .imageContainer {
        max-width: 100%;
        // horizontally center
        margin: 0 auto;

        @media (min-width: 770px) {
          &.quarter {
            max-width: 25%;
          }
          &.third {
            max-width: 33%;
          }
          &.half {
            max-width: 50%;
          }
          &.two-thirds {
            max-width: 66%;
          }
          &.three-quarters {
            max-width: 75%;
          }
        }
      }
    }

    &.bottomImageCard > .imageContainer {
      // Move image to second row
      grid-row: 2;
    }

    // Base styles applied to both
    &.leftImageCard,
    &.rightImageCard {
      grid-template-rows: 1fr;

      // Grid setup for tablet and up, falls back to default two column grid
      @media (min-width: 770px) {
        grid-template-columns: repeat(12, 1fr);
      }

      > .imageContainer,
      > .textContainer {
        // vertically center images for left and right image cards
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }

    &.leftImageCard {
      > .imageContainer {
        ${leftGridContentCSS}
      }

      > .textContainer {
        grid-row: 2;
        ${rightGridContentCSS}
      }
    }

    &.rightImageCard {
      > .imageContainer {
        ${rightGridContentCSS}
      }

      > .textContainer {
        grid-row: 2;
        ${leftGridContentCSS}
      }
    }
  }
`;
